// Loan health dashboard — Overview scorecard + needs-attention feed in one
// round trip (GET /portfolio-admin/loans/{id}/health). Severity values are
// server-computed mirrors of the per-tab badge rules (LoanHealthService);
// the FE never re-derives them.

import { api } from "@/api/client";

export type DisciplineSeverity = "ok" | "warning" | "error" | "unavailable";
export type AttentionSeverity = "error" | "warning";

export type HealthDiscipline =
  | "payments"
  | "insurance"
  | "ucc"
  | "covenants"
  | "reporting"
  | "vendors"
  | "qa"
  | "defaults";

export interface LoanDisciplineHealthDto {
  discipline: HealthDiscipline;
  severity: DisciplineSeverity;
  count: number;
  headline: string;
}

export interface LoanAttentionItemDto {
  id: string;
  /** A health discipline, or "other" for unmapped calendar ticklers. */
  discipline: string;
  severity: AttentionSeverity;
  title: string;
  /** ISO date (YYYY-MM-DD); null for dateless rows (open claims, …). */
  dueDate: string | null;
  ownerRole: string;
  /** Loan tab key for click-through. */
  targetTab: string;
  /** Set when the row is a compliance-calendar tickler (complete/snooze). */
  calendarItemId: string | null;
  /** "breach" = open covenant-breach condition (DSCR Fail/Watch, open
   *  LoanFailures, vacant required-vendor slots) — what the Compliance
   *  tab's breach strip renders. Optional so fixtures predating the field
   *  stay valid; the server always sends it. */
  kind?: "breach" | "other";
}

/** The composite Loan Health Grade derived server-side from the discipline
 *  lights (rubric: Core LoanHealthGrade — the grade can never disagree with
 *  the scorecard). grade null = not graded (paid-off / liquidated).
 *  appliedCap = the account state whose cap LOWERED the count-derived
 *  grade (null when no cap bound). */
export interface LoanHealthGradeDto {
  grade: "A" | "B" | "C" | "D" | "F" | null;
  warningCount: number;
  errorCount: number;
  appliedCap: string | null;
  accountState: string | null;
}

export interface LoanHealthDto {
  loanId: string;
  disciplines: LoanDisciplineHealthDto[];
  attention: LoanAttentionItemDto[];
  grade: LoanHealthGradeDto | null;
}

/** One row of GET /portfolio-admin/loans/health-grades — the same per-loan
 *  computation as the single-loan endpoint (no rule fork), plus the ledger
 *  vitals the list columns need (null when the payments source failed). */
export interface LoanGradeRowDto {
  loanId: string;
  grade: LoanHealthGradeDto;
  disciplines: LoanDisciplineHealthDto[];
  daysDelinquent: number | null;
  nextDueDate: string | null;
  scheduledMonthlyPayment: number | null;
  receivedYearToDate: number | null;
  attention: LoanAttentionItemDto[];
}

export const loanHealthApi = {
  get: (loanId: string) =>
    api.get<LoanHealthDto>(`/portfolio-admin/loans/${loanId}/health`),
  grades: (loanIds: string[]) =>
    api.get<LoanGradeRowDto[]>(`/portfolio-admin/loans/health-grades`, {
      loanId: loanIds,
    }),
};
